import React, { createContext, useContext, useState, useEffect } from "react";
import { getProfile } from "../utils/api"; // Function to fetch user profile
import { useLocalStorage } from "../hooks";
import LoadingIcon from "../components/LoadingIcon";
import { ORGANIZATION } from "../consts";
import * as Sentry from "@sentry/react";

const UserContext = createContext({});

export const useUser = () => useContext(UserContext);

const UserContextProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true); // Start with true to indicate data fetching
  const [accessToken] = useLocalStorage("accessToken", null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsFetchingProfile(true);
      try {
        const response = await getProfile(accessToken);
        const data = response?.data;
        if (data && data.data) {
          setUserData(data.data);

          const u = data.data;
          Sentry.setUser({
            org_name: u.organization?.name,
            site_name: u.site?.name,
            email: u.email,
            username: u.email,
            is_superuser: Boolean(u.is_superuser),
            is_admin: Boolean(u.is_admin),
            is_grower: Boolean(u.organization?.is_grower),
            is_breeder: Boolean(u.organization?.is_grower),
            is_pollination: Boolean(u.organization?.is_pollination),
          });

          Sentry.setTags({
            org_name: u.organization?.name,
            site_name: u.site?.name,
            is_grower: String(u.organization?.is_grower),
            is_breeder: String(u.organization?.is_grower),
            is_pollination: String(u.organization?.is_pollination),
            is_admin: String(u?.is_admin),
            is_superuser: String(u.is_superuser),
          });
        } else {
          setUserData(null);
          Sentry.setUser(null);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setUserData(null);
        Sentry.setUser(null);
      } finally {
        setIsFetchingProfile(false);
      }
    };

    if (accessToken) {
      fetchUserProfile();
    } else {
      setIsFetchingProfile(false);
    }
  }, [accessToken]);

  const isAuthenticated = () => !!accessToken;

  const isAdmin = () => {
    return (
      userData?.is_admin ||
      userData?.is_superuser ||
      (userData?.site_admins && userData?.site_admins.length > 0)
    );
  };

  const userPermissions = () => {
    return userData?.permissions || [];
  };

  const hasOrgPermission = (orgType) => {
    const userOrgType = [];
    if (userData?.organization?.is_breeder) {
      userOrgType.push(ORGANIZATION.BREEDER);
    }
    if (userData?.organization?.is_grower) {
      userOrgType.push(ORGANIZATION.GROWER);
    }
    if (userData?.organization?.is_pollination) {
      userOrgType.push(ORGANIZATION.POLLINATION);
    }
    return userOrgType.includes(orgType);
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        isAuthenticated,
        isAdmin,
        hasOrgPermission,
        userPermissions,
        setUser: setUserData,
        isFetchingProfile,
        clearUser: () => setUserData(null),
      }}
    >
      {isFetchingProfile ? (
        <LoadingIcon height="200px" classname="absolute h-full" />
      ) : (
        children
      )}
    </UserContext.Provider>
  );
};

export { UserContextProvider, UserContext };
